import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from './lib/cn';

export interface ToastProps {
  type?: 'success' | 'danger' | 'info' | 'warning';
  title: string;
  description?: ReactNode;
  onClose?: () => void;
  action?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };
  className?: string;
}

const typeStyles = {
  success: {
    border: 'border-[color-mix(in_srgb,var(--success)_28%,var(--border))]',
    bg: 'bg-[var(--success-soft)]',
    text: 'text-[var(--success)]',
    iconBg: 'bg-[color-mix(in_srgb,var(--success)_12%,var(--surface))]',
    icon: CheckCircle2,
  },
  danger: {
    border: 'border-[color-mix(in_srgb,var(--danger)_28%,var(--border))]',
    bg: 'bg-[var(--danger-soft)]',
    text: 'text-[var(--danger)]',
    iconBg: 'bg-[color-mix(in_srgb,var(--danger)_12%,var(--surface))]',
    icon: AlertCircle,
  },
  warning: {
    border: 'border-[color-mix(in_srgb,var(--warning)_28%,var(--border))]',
    bg: 'bg-[var(--warning-soft)]',
    text: 'text-[var(--warning)]',
    iconBg: 'bg-[color-mix(in_srgb,var(--warning)_12%,var(--surface))]',
    icon: AlertCircle,
  },
  info: {
    border: 'border-[color-mix(in_srgb,var(--accent)_28%,var(--border))]',
    bg: 'bg-[var(--accent-soft)]',
    text: 'text-[var(--accent)]',
    iconBg: 'bg-[color-mix(in_srgb,var(--accent)_12%,var(--surface))]',
    icon: Info,
  },
};

export function Toast({
  type = 'info',
  title,
  description,
  onClose,
  action,
  className,
}: ToastProps) {
  const style = typeStyles[type] || typeStyles.info;
  const IconComponent = style?.icon || Info || (() => null);
  const CloseIcon = X || (() => null);
  const isUrgent = type === 'danger' || type === 'warning';

  return (
    <div
      role={isUrgent ? 'alert' : 'status'}
      aria-live={isUrgent ? 'assertive' : 'polite'}
      className={cn(
        'motion-fade-in flex items-start gap-3 rounded-2xl border p-3.5 shadow-[var(--shadow-sm)] sm:p-4',
        style.bg,
        style.border,
        className,
      )}
    >
      <span
        className={cn(
          'grid size-10 shrink-0 place-items-center rounded-xl',
          style.iconBg,
          style.text,
        )}
      >
        <IconComponent className="size-5" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1 py-0.5">
        <h4 className="text-xs font-black tracking-wide text-[var(--foreground)]">{title}</h4>
        {description && (
          <p className="mt-1 text-xs leading-5 text-[var(--muted-foreground)]">{description}</p>
        )}
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            disabled={action.disabled}
            className={cn(
              'mt-2 min-h-10 rounded-xl px-3 text-xs font-extrabold transition-colors duration-200 hover:bg-[color-mix(in_srgb,var(--surface)_72%,transparent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] disabled:pointer-events-none disabled:opacity-45',
              style.text,
            )}
          >
            {action.label}
          </button>
        )}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar notificação"
          className="grid size-11 shrink-0 place-items-center rounded-xl text-[var(--muted-foreground)] transition-colors duration-200 hover:bg-[color-mix(in_srgb,var(--surface)_64%,transparent)] hover:text-[var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
        >
          <CloseIcon className="size-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
